// synthetic-arabic: reviewed-no-scripture — the Arabic here is ordinary product interface wording
// invented for this application; it contains no Qur'an, hadith, translation or religious quotation.
import { messages } from './i18n.js';

/**
 * Everything the application shell says, in both languages (founder, 2026-09-13).
 *
 * The Arabic is the product's own wording, moved here unchanged rather than rewritten: this is a
 * translation, not a redesign, and a line that a reviewer or the founder approved in Arabic must
 * still read exactly the same. The English is written to say the same thing in the same register —
 * plain, short, and making no claim the Arabic does not make. Anything that names what Bilal is not
 * is a claim-control sentence (`INTENDED_USE.md`) and is translated literally.
 *
 * Interpolated lines are functions, so no language is forced into another's word order.
 */
type CodeWords = Readonly<Record<string, string>>;

/** Keyed by the closed codes the callback route answers with (ADR-033); looked up by string. */
const AR_OAUTH: CodeWords = {
  state_mismatch: 'انتهت محاولة الدخول أو لم تكتمل. حاول مرة أخرى.',
  cancelled: 'ألغيت الدخول عبر Google.',
  refused: 'لم يقبل Google هذه المحاولة. حاول مرة أخرى.',
  provider_unavailable: 'Google غير متاح الآن. حاول لاحقًا أو سجّل الدخول بالبريد.',
  adult_attestation_required: 'هذا التطبيق للبالغين (18 عامًا فأكثر).',
  unavailable: 'هذه الطريقة في الدخول غير متاحة في هذه النسخة.',
};
const EN_OAUTH: CodeWords = {
  state_mismatch: 'That sign-in attempt expired or did not finish. Try again.',
  cancelled: 'Signing in with Google was cancelled.',
  refused: 'Google refused that attempt. Try again.',
  provider_unavailable: 'Google is unavailable right now. Try later, or sign in with email.',
  adult_attestation_required: 'This application is for adults (18 and over).',
  unavailable: 'That way in is not available in this build.',
};

/** The closed refusal codes the account routes answer with (ADR-025, ADR-034). */
const AR_AUTHERRORS: CodeWords = {
  invalid_email: 'أدخل بريدًا إلكترونيًا صحيحًا.',
  invalid_password: 'كلمة المرور يجب أن تكون 4 أحرف على الأقل.',
  adult_attestation_required: 'هذا التطبيق للبالغين (18 عامًا فأكثر).',
  email_taken: 'هذا البريد مسجّل بالفعل. جرّب تسجيل الدخول.',
  invalid_name: 'اكتب الاسم الذي تودّ أن يناديك به بلال.',
  invalid_gender: 'اختر كيف يخاطبك بلال.',
  invalid_age_range: 'اختر فئتك العمرية.',
  invalid_country: 'اختر بلدك.',
  invalid_referral: 'أخبرنا كيف عرفت بلال.',
  invalid_interest: 'أخبرنا ما الذي جئت من أجله.',
  code_invalid: 'الرمز غير صحيح. تحقّق منه وحاول مجددًا.',
  code_expired: 'انتهت صلاحية الرمز. اطلب رمزًا جديدًا.',
  too_many_attempts: 'محاولات كثيرة على هذا الرمز. اطلب رمزًا جديدًا.',
  too_soon: 'أُرسل رمز قبل قليل. انتظر دقيقة ثم أعد الإرسال.',
  too_many_requests: 'طلبات كثيرة لهذا البريد. حاول بعد ساعة.',
  email_delivery_unavailable: 'إنشاء الحساب بالبريد غير متاح الآن. استخدم Google أو حاول لاحقًا.',
  email_delivery_failed: 'تعذّر إرسال الرمز إلى هذا البريد. تحقّق من العنوان وحاول مجددًا.',
  no_account: 'لا يوجد حساب بهذا البريد. أنشئ حسابًا جديدًا.',
  link_invalid: 'انتهت صلاحية هذا الرابط أو استُخدم من قبل. اطلب رابطًا جديدًا.',
  link_expired: 'انتهت صلاحية هذا الرابط. اطلب رابطًا جديدًا.',
  invalid_credentials: 'البريد الإلكتروني أو كلمة المرور غير صحيحة.',
  rate_limited: 'محاولات كثيرة. انتظر قليلًا ثم حاول مجددًا.',
};
const EN_AUTHERRORS: CodeWords = {
  invalid_email: 'Enter a valid email address.',
  invalid_password: 'The password must be at least 4 characters.',
  adult_attestation_required: 'This application is for adults (18 and over).',
  email_taken: 'That email already has an account. Try signing in.',
  invalid_name: 'Write the name you would like Bilal to call you.',
  invalid_gender: 'Choose how Bilal should address you.',
  invalid_age_range: 'Choose your age range.',
  invalid_country: 'Choose your country.',
  invalid_referral: 'Tell us how you heard of Bilal.',
  invalid_interest: 'Tell us what you came for.',
  code_invalid: 'That code is not right. Check it and try again.',
  code_expired: 'That code has expired. Ask for a new one.',
  too_many_attempts: 'Too many attempts on this code. Ask for a new one.',
  too_soon: 'A code was sent a moment ago. Wait a minute, then send again.',
  too_many_requests: 'Too many requests for this address. Try again in an hour.',
  email_delivery_unavailable:
    'Creating an account by email is unavailable right now. Use Google, or try later.',
  email_delivery_failed: 'The code could not be sent to that address. Check it and try again.',
  no_account: 'No account has that email. Create one.',
  link_invalid: 'That link has expired or was already used. Ask for a new one.',
  link_expired: 'That link has expired. Ask for a new one.',
  invalid_credentials: 'That email or password is not right.',
  rate_limited: 'Too many attempts. Wait a moment, then try again.',
};

export const MESSAGES = messages({
  ar: {
    appName: 'بلال',
    menu: 'القائمة',
    retry: 'أعد الإرسال',
    positioning: 'ليس معالجًا نفسيًا وليس مفتيًا؛ لا يشخّص ولا يعالج ولا يصدر فتوى.',

    door: {
      region: 'الدخول إلى بلال',
      welcomeBack: 'مرحبًا بعودتك',
      resetTitle: 'استعادة كلمة المرور',
      createTitle: 'أنشئ حسابك',
      signInHint: 'سجّل الدخول لمتابعة رحلتك مع بلال.',
      resetHint: 'نرسل رمزًا إلى بريدك، ثم تختار كلمة مرور جديدة.',
      createHint: 'رفيقك للتفكير في أمور حياتك اليومية.',
      google: 'المتابعة عبر Google',
      apple: 'المتابعة عبر Apple',
      or: 'أو',
      resetUnavailable:
        'استعادة كلمة المرور بالبريد غير متاحة في هذه النسخة حاليًا. عُد لاحقًا أو استخدم المتابعة عبر Google إن ظهرت.',
      email: 'البريد الإلكتروني',
      passwordLabel: 'كلمة المرور',
      newPassword: 'كلمة المرور الجديدة',
      code: 'رمز التحقق',
      sendCode: 'أرسل رمز التحقق',
      setPassword: 'تعيين كلمة المرور',
      signIn: 'تسجيل الدخول',
      createAccount: 'إنشاء الحساب',
      resendCode: 'أعد إرسال الرمز',
      resendIn: (seconds: number) => `إعادة الإرسال بعد ${String(seconds)} ث`,
      editEmail: 'تعديل البريد',
      forgot: 'نسيت كلمة المرور؟',
      toRegister: 'ليس لديك حساب؟ أنشئ حسابًا',
      toSignIn: 'لديك حساب؟ سجّل الدخول',
      backToSignIn: 'العودة إلى تسجيل الدخول',
      developmentEntry: 'تسجيل تجريبي فوري',
      codeSentReset: (address: string) =>
        `أرسلنا رمزًا من ستة أرقام إلى ${address}. أدخله واختر كلمة مرور جديدة.`,
      codeSentRegister: (address: string) =>
        `أرسلنا رمزًا من ستة أرقام إلى ${address}. أدخله هنا لإتمام إنشاء حسابك.`,
      linkAsk: 'أرسل لي رابط دخول بدل كلمة المرور',
      linkNeedsEmail: 'اكتب بريدك أولًا، ثم نرسل إليه الرابط.',
      linkSent: (address: string) =>
        `أرسلنا رابط الدخول إلى ${address}. افتحه من هذا الجهاز لتدخل مباشرة. الرابط صالح لمدة 15 دقيقة ويُستخدم مرة واحدة.`,
      linkResend: 'أعد إرسال الرابط',
      linkOpenTitle: 'رابط الدخول',
      linkOpenHint: 'اضغط للدخول إلى حسابك في بلال.',
      linkOpen: 'الدخول إلى بلال',
      linkOpening: 'جارٍ الدخول…',
      attestationBefore: 'بالمتابعة تقرّ بأنك بلغت 18 عامًا وتوافق على ',
      attestationTerms: 'شروط الاستخدام',
      attestationAnd: ' و',
      attestationPrivacy: 'سياسة الخصوصية',
      attestationAfter: '.',
    },

    profileCard: {
      region: 'أكمل بياناتك',
      title: 'قبل أن نبدأ',
      hint: 'سؤالان فقط، حتى يخاطبك بلال كما تحب.',
      submit: 'متابعة',
      moreTitle: 'سؤال أخير، إن أحببت',
      moreHint: 'اختياري تمامًا: يساعدنا على أن نفهم من نخدم. تستطيع تغييره أو تركه في أي وقت.',
      save: 'حفظ ومتابعة',
      skip: 'تخطٍّ',
    },

    chat: {
      session: 'جلسة الحوار',
      scroll: 'سجل المحادثة',
      list: 'المحادثة',
      waiting: 'بلال ينتظر…',
      memorySaved: 'حُفظ في الذاكرة ✓',
      planSent: 'رتّبت البنود',
      planAsSent: (steps: string) => `الخطة كما أرسلتها:\n${steps}`,
    },

    memory: {
      region: 'الذاكرة',
      title: 'الذاكرة',
      empty: 'لا شيء محفوظ بعد.',
      list: 'الملف الشخصي',
    },

    consent: {
      region: 'بيانات الاستخدام',
      line: 'نقيس الاستخدام بشكل مجهول لتحسين بلال، ولا يُرسل نص محادثاتك أبدًا. يمكنك إيقاف ذلك متى شئت.',
      details: 'التفاصيل',
      acknowledge: 'حسنًا',
      turnOff: 'إيقاف القياس',
    },

    voice: {
      reviewHint: 'لم أتأكد من بعض الكلمات — راجع النص ثم أرسل.',
      liveUnavailable: 'التحويل الفوري غير متاح الآن. اكتب رسالتك.',
      liveFailed: 'تعذّر التحويل الفوري. أعد المحاولة أو اكتب رسالتك.',
      nothingHeard: 'لم يُلتقط أي كلام. حاول مرة أخرى أو اكتب رسالتك.',
      nothingRecorded: 'لم يُسجَّل أي صوت. حاول مرة أخرى.',
      transcriptionFailed: 'تعذّر تحويل الصوت إلى نص. اكتب رسالتك أو أعد المحاولة.',
      microphoneDenied: 'تعذّر الوصول إلى الميكروفون. راجع أذونات المتصفح.',
      microphoneUnavailable: 'لم يستجب الميكروفون. تحقّق من الجهاز، أو اكتب رسالتك.',
      alreadyRecording: 'التسجيل جارٍ بالفعل.',
    },

    confirm: {
      // Honest about what happens (ADR-042): gone from the account now, kept for the team's
      // review for thirty days, then erased. A confirmation that said "permanently" would be a lie
      // told at the most sensitive moment the product has.
      eraseConversations: (count: number) =>
        `حذف ${String(count)} محادثة من حسابك، ومعها كل ما تذكّره بلال منها؟ لن تستطيع استعادتها. قد يراجعها فريق بلال لتحسين الخدمة مدة تصل إلى 30 يومًا، ثم تُمحى نهائيًا. لمحو كل شيء فورًا احذف حسابك.`,
      forgetProfile: 'حذف كل ما يتذكّره بلال عنك؟ المحادثات نفسها تبقى. لا يمكن التراجع.',
      deleteAccount:
        'حذف حسابك نهائيًا، ومعه كل محادثاتك وكل ما يتذكّره بلال عنك؟ لا يمكن التراجع.',
      deleteConversation:
        'حذف هذه المحادثة من حسابك؟ لن تستطيع استعادتها. قد يراجعها فريق بلال لتحسين الخدمة مدة تصل إلى 30 يومًا، ثم تُمحى نهائيًا. لمحو كل شيء فورًا احذف حسابك من الإعدادات.',
    },

    failure: {
      generic: 'تعذّر إتمام العملية. حاول مرة أخرى.',
      service: 'تعذّر الوصول إلى الخدمة الآن.',
      session: 'تعذّر بدء الجلسة. حاول مرة أخرى.',
      saveDetails: 'تعذّر حفظ البيانات. حاول مرة أخرى.',
      sendCode: 'تعذّر إرسال الرمز. حاول مرة أخرى.',
      setPassword: 'تعذّر تعيين كلمة المرور. حاول مرة أخرى.',
      google: 'تعذّر تسجيل الدخول عبر Google. حاول مرة أخرى.',
      closedConversation:
        'لم تُرسل الرسائل التي كتبتها بعد ذلك، لأن هذه المحادثة انتهت. ابدأ محادثة جديدة.',
      developmentSignIn: (code: string) => `تعذّر تسجيل الدخول التطويري: ${code}`,
      createSession: (code: string) => `تعذّر إنشاء الجلسة: ${code}`,
      newConversation: (code: string) => `تعذّر إنشاء محادثة جديدة: ${code}`,
      erase: (code: string) => `تعذّر الحذف: ${code}`,
      forget: (code: string) => `تعذّر حذف الذاكرة: ${code}`,
      deleteAccount: (code: string) => `تعذّر حذف الحساب: ${code}`,
      deleteConversation: (code: string) => `تعذّر حذف المحادثة: ${code}`,
    },

    passwordSet: 'تم تعيين كلمة المرور الجديدة. سجّل الدخول بها.',

    /** What a return from an outside door says. Closed codes from the callback route (ADR-033). */
    oauth: AR_OAUTH,

    /** The closed refusal codes the account routes answer with (ADR-025, ADR-034). */
    authErrors: AR_AUTHERRORS,
  },

  en: {
    appName: 'Bilal',
    menu: 'Menu',
    retry: 'Send again',
    positioning:
      'Not a therapist and not a mufti; it does not diagnose, treat or issue religious rulings.',

    door: {
      region: 'Sign in to Bilal',
      welcomeBack: 'Welcome back',
      resetTitle: 'Reset your password',
      createTitle: 'Create your account',
      signInHint: 'Sign in to carry on where you left off.',
      resetHint: 'We send a code to your email, then you choose a new password.',
      createHint: 'A companion for thinking through everyday life.',
      google: 'Continue with Google',
      apple: 'Continue with Apple',
      or: 'or',
      resetUnavailable:
        'Resetting a password by email is not available in this build yet. Come back later, or continue with Google if it is offered.',
      email: 'Email',
      passwordLabel: 'Password',
      newPassword: 'New password',
      code: 'Verification code',
      sendCode: 'Send the code',
      setPassword: 'Set the password',
      signIn: 'Sign in',
      createAccount: 'Create account',
      resendCode: 'Send the code again',
      resendIn: (seconds: number) => `Send again in ${String(seconds)}s`,
      editEmail: 'Change the email',
      forgot: 'Forgotten your password?',
      toRegister: 'No account yet? Create one',
      toSignIn: 'Already have an account? Sign in',
      backToSignIn: 'Back to sign in',
      developmentEntry: 'Instant development sign-in',
      codeSentReset: (address: string) =>
        `We sent a six-digit code to ${address}. Enter it and choose a new password.`,
      codeSentRegister: (address: string) =>
        `We sent a six-digit code to ${address}. Enter it here to finish creating your account.`,
      linkAsk: 'Email me a sign-in link instead',
      linkNeedsEmail: 'Write your email first, and the link goes there.',
      linkSent: (address: string) =>
        `We sent a sign-in link to ${address}. Open it on this device to come straight in. It is valid for 15 minutes and works once.`,
      linkResend: 'Send the link again',
      linkOpenTitle: 'Your sign-in link',
      linkOpenHint: 'Press to come in to your Bilal account.',
      linkOpen: 'Sign in to Bilal',
      linkOpening: 'Signing you in…',
      attestationBefore: 'By continuing you confirm you are 18 or over and accept the ',
      attestationTerms: 'terms of use',
      attestationAnd: ' and the ',
      attestationPrivacy: 'privacy policy',
      attestationAfter: '.',
    },

    profileCard: {
      region: 'Complete your details',
      title: 'Before we start',
      hint: 'Two questions only, so Bilal addresses you the way you prefer.',
      submit: 'Continue',
      moreTitle: 'One last thing, if you like',
      moreHint:
        'Entirely optional: it helps us understand who we serve. You can change it or leave it out at any time.',
      save: 'Save and continue',
      skip: 'Skip',
    },

    chat: {
      session: 'Conversation',
      scroll: 'Conversation history',
      list: 'Conversation',
      waiting: 'Bilal is waiting…',
      memorySaved: 'Saved to memory ✓',
      planSent: 'I reordered the steps',
      planAsSent: (steps: string) => `The plan as I sent it:\n${steps}`,
    },

    memory: {
      region: 'Memory',
      title: 'Memory',
      empty: 'Nothing saved yet.',
      list: 'Profile',
    },

    consent: {
      region: 'Usage data',
      line: 'We measure usage anonymously to improve Bilal, and the text of your conversations is never sent. You can turn this off whenever you like.',
      details: 'Details',
      acknowledge: 'OK',
      turnOff: 'Turn off',
    },

    voice: {
      reviewHint: 'Some words were not certain — check the text, then send.',
      liveUnavailable: 'Live transcription is unavailable right now. Type your message instead.',
      liveFailed: 'Live transcription failed. Try again, or type your message.',
      nothingHeard: 'Nothing was heard. Try again, or type your message.',
      nothingRecorded: 'Nothing was recorded. Try again.',
      transcriptionFailed:
        'The recording could not be transcribed. Type your message, or try again.',
      microphoneDenied: 'The microphone could not be reached. Check your browser permissions.',
      microphoneUnavailable: 'The microphone did not respond. Check the device, or type instead.',
      alreadyRecording: 'A recording is already running.',
    },

    confirm: {
      eraseConversations: (count: number) =>
        `Delete ${String(count)} conversation(s) from your account, with everything Bilal remembered from them? You will not be able to get them back. The Bilal team may still review them, to improve the service, for up to 30 days; then they are erased for good. To erase everything at once, delete your account.`,
      forgetProfile:
        'Delete everything Bilal remembers about you? The conversations themselves stay. This cannot be undone.',
      deleteAccount:
        'Permanently delete your account, with every conversation and everything Bilal remembers about you? This cannot be undone.',
      deleteConversation:
        'Delete this conversation from your account? You will not be able to get it back. The Bilal team may still review it, to improve the service, for up to 30 days; then it is erased for good. To erase everything at once, delete your account in the settings.',
    },

    failure: {
      generic: 'That did not go through. Try again.',
      service: 'The service cannot be reached right now.',
      session: 'The session could not be started. Try again.',
      saveDetails: 'Your details could not be saved. Try again.',
      sendCode: 'The code could not be sent. Try again.',
      setPassword: 'The password could not be set. Try again.',
      google: 'Signing in with Google did not work. Try again.',
      closedConversation:
        'What you typed after that was not sent, because this conversation had ended. Start a new one.',
      developmentSignIn: (code: string) => `Development sign-in failed: ${code}`,
      createSession: (code: string) => `The session could not be created: ${code}`,
      newConversation: (code: string) => `A new conversation could not be started: ${code}`,
      erase: (code: string) => `Deletion failed: ${code}`,
      forget: (code: string) => `The memory could not be deleted: ${code}`,
      deleteAccount: (code: string) => `The account could not be deleted: ${code}`,
      deleteConversation: (code: string) => `The conversation could not be deleted: ${code}`,
    },

    passwordSet: 'Your new password is set. Sign in with it.',

    oauth: EN_OAUTH,

    authErrors: EN_AUTHERRORS,
  },
});
